/**
 * BigNumber Firestore Converter (Client SDK)
 *
 * Client-side converter for use with firebase/firestore (NOT firebase-admin).
 * Converts between BigNumber TypeScript type and Firestore data.
 *
 * Based on strategyConverter pattern from firebase-core module.
 *
 * CRITICAL: Uses Client SDK signature fromFirestore(snapshot, options)
 * NOT Admin SDK signature fromFirestore(data)
 */

import { FirestoreDataConverter, QueryDocumentSnapshot, SnapshotOptions, Timestamp, WithFieldValue } from 'firebase/firestore';
import type { BigNumber } from '@segmently/schema-models/entities';

export const bigNumberConverter: FirestoreDataConverter<BigNumber> = {
  /**
   * Convert BigNumber model to Firestore data
   * Called when writing to Firestore (create, update operations)
   *
   * Date Field Mapping:
   * - createdAt (string/Date) → createdAt (Timestamp)
   */
  toFirestore(bigNumber: WithFieldValue<BigNumber>): any {
    const result: any = { ...bigNumber };

    // Convert Date/string to Firestore Timestamp for createdAt
    if (typeof result['createdAt'] === 'string') {
      result['createdAt'] = Timestamp.fromDate(new Date(result['createdAt']));
    } else if (result['createdAt'] instanceof Date) {
      result['createdAt'] = Timestamp.fromDate(result['createdAt']);
    }

    return result;
  },

  /**
   * Convert Firestore data to BigNumber model
   * Called when reading from Firestore (get, query, snapshot operations)
   *
   * CRITICAL: Uses Client SDK signature (snapshot, options) NOT Admin SDK signature (data)
   *
   * Date Field Mapping:
   * - createdAt (Timestamp) → createdAt (ISO string)
   */
  fromFirestore(
    snapshot: QueryDocumentSnapshot,
    options?: SnapshotOptions
  ): BigNumber {
    // Extract data from snapshot using Client SDK method
    const data = snapshot.data(options);
    const result: any = { ...data };

    // Add document ID to result
    result.id = snapshot.id;

    // Convert Firestore Timestamp to ISO string for createdAt
    if (result['createdAt']?.toDate) {
      result['createdAt'] = result["createdAt"].toDate();
    }

    return result as BigNumber;
  }
};

export default bigNumberConverter;
